import React, { useRef, useEffect, useMemo, useCallback } from 'react';
import dayjs from 'dayjs';
import OwnerEventBlock from './OwnerEventBlock';
import { useOwnerCalendar } from '../../../context/OwnerCalendarContext';
import { useOwnerCalendarView } from '../../../hooks/calendar/useOwnerCalendarView';
import { useOwnerBookingLogic } from '../../../hooks/calendar/useOwnerBookingLogic';
import { getNonWorkingBlocks, timeToPixels } from '../../../utils/calendar/timeCalculations';
import { getServiceDuration } from '../../../utils/calendar/durationService';

const dayNamesPl = ['Pon', 'Wt', 'Śr', 'Czw', 'Pt', 'Sob', 'Nie'];

const OwnerCalendarGrid = ({
                               workingHours,
                               trainings,
                               trainingLocks,
                               confirmedTrainings,
                               slotBookings,
                               selectedService,
                               onSlotClick,
                               onCancelTraining
                           }) => {
    const {
        currentWeekStart,
        selectedTrainer,
        selectedSlots,
        bookingMode,
        schoolSettings,
        toggleSlotSelection,
        isSlotSelected,
    } = useOwnerCalendar();

    const {
        timeRange,
        gridDimensions,
        daysOfWeek,
    } = useOwnerCalendarView(workingHours, currentWeekStart);

    const {
        getSlotStatus,
        getTimeSlotsForDay,
        getFreeSlotsForDay,
        isMonthBlocked,
        getSlotBookingInfo,
    } = useOwnerBookingLogic({
        trainings,
        trainingLocks,
        confirmedTrainings,
        slotBookings,
        workingHours,
        selectedService
    });

    const serviceDurationMinutes = selectedService ? getServiceDuration(selectedService) : 60;
    const slotHeight = (serviceDurationMinutes / 60) * gridDimensions.hourHeight;

    const handleSlotClick = useCallback((slotTime) => {
        const slotStatus = getSlotStatus(slotTime);

        if (slotStatus.type === 'free' && slotStatus.canBook) {
            if (bookingMode === 'multiple') {
                toggleSlotSelection(slotTime);
            } else if (onSlotClick) {
                onSlotClick(slotTime);
            }
        }
    }, [bookingMode, toggleSlotSelection, onSlotClick, getSlotStatus]);

    const renderDayContent = useCallback((dayInfo) => {
        const { date: dayDate } = dayInfo;
        const daySlots = getTimeSlotsForDay(dayDate, serviceDurationMinutes);

        if (isMonthBlocked(dayDate)) {
            return (
                <div className="owner-day-content" style={{ height: `${gridDimensions.totalHeight}px` }}>
                    <div className="owner-no-hours">Rezerwacje na ten miesiąc są zablokowane</div>
                </div>
            );
        }

        if (!selectedTrainer) {
            return (
                <div className="owner-day-content" style={{ height: `${gridDimensions.totalHeight}px` }}>
                    <div className="owner-no-hours">Wybierz trenera</div>
                </div>
            );
        }

        if (daySlots.length === 0) {
            return (
                <div className="owner-day-content" style={{ height: `${gridDimensions.totalHeight}px` }}>
                    <div className="owner-no-hours">Brak godzin pracy</div>
                </div>
            );
        }

        const dayStartTime = dayDate.hour(parseInt(timeRange.earliestTime.split(':')[0])).minute(0).second(0);
        const nonWorkingBlocks = getNonWorkingBlocks(dayDate, workingHours, timeRange);

        // Group consecutive unavailable/locked/occupied slots together
        const groupedSlots = [];
        let currentGroup = null;

        daySlots.forEach(({ time: slotTime, status }, index) => {
            // Include all non-bookable slots except owner's own bookings
            const isUnavailable = status.type === 'unavailable' ||
                status.type === 'past' ||
                status.type === 'locked' ||
                status.type === 'confirmed-other';

            if (isUnavailable) {
                if (!currentGroup || currentGroup.type !== 'unavailable-group') {
                    currentGroup = {
                        type: 'unavailable-group',
                        startTime: slotTime,
                        endTime: slotTime.add(serviceDurationMinutes, 'minute'),
                        slots: [{ time: slotTime, status }]
                    };
                    groupedSlots.push(currentGroup);
                } else {
                    currentGroup.endTime = slotTime.add(serviceDurationMinutes, 'minute');
                    currentGroup.slots.push({ time: slotTime, status });
                }
            } else {
                currentGroup = null;
                groupedSlots.push({
                    type: 'single',
                    time: slotTime,
                    status
                });
            }
        });

        return (
            <div className="owner-day-content" style={{ height: `${gridDimensions.totalHeight}px`, position: 'relative' }}>
                {nonWorkingBlocks.map((block, index) => {
                    const blockTop = timeToPixels(block.start, dayStartTime, gridDimensions.hourHeight);
                    const blockHeight = timeToPixels(block.end, dayStartTime, gridDimensions.hourHeight) - blockTop;

                    return (
                        <div
                            key={`non-working-${index}`}
                            className={`owner-non-working-hours ${block.type}`}
                            style={{
                                position: 'absolute',
                                top: `${blockTop}px`,
                                left: 0,
                                right: 0,
                                height: `${blockHeight}px`,
                                background: 'rgba(0, 0, 0, 0.05)',
                                pointerEvents: 'none',
                                zIndex: 1,
                            }}
                        />
                    );
                })}

                <div className="owner-time-grid">
                    {groupedSlots.map((item, index) => {
                        if (item.type === 'unavailable-group') {
                            const startHour = item.startTime.hour();
                            const startMinute = item.startTime.minute();
                            const startHourOffset = parseInt(timeRange.earliestTime.split(':')[0]);

                            const hoursFromStart = startHour - startHourOffset;
                            const minutesOffset = startMinute / 60;
                            const totalOffset = (hoursFromStart + minutesOffset) * gridDimensions.hourHeight;

                            const groupHeight = item.slots.length * slotHeight;

                            return (
                                <div
                                    key={`unavailable-group-${index}`}
                                    className="owner-time-slot unavailable-merged"
                                    style={{
                                        position: 'absolute',
                                        top: `${totalOffset}px`,
                                        left: '2px',
                                        right: '2px',
                                        height: `${groupHeight}px`,
                                        zIndex: 2,
                                    }}
                                >
                                    <div className="owner-event-block owner-event-unavailable-merged">
                                        <div className="owner-event-title">Niedostępne</div>
                                        <div className="owner-event-time">
                                            {item.startTime.format('HH:mm')} - {item.endTime.format('HH:mm')}
                                        </div>
                                    </div>
                                </div>
                            );
                        } else {
                            const { time: slotTime, status } = item;
                            const slotHour = slotTime.hour();
                            const slotMinute = slotTime.minute();
                            const startHour = parseInt(timeRange.earliestTime.split(':')[0]);

                            const hoursFromStart = slotHour - startHour;
                            const minutesOffset = slotMinute / 60;
                            const totalOffset = (hoursFromStart + minutesOffset) * gridDimensions.hourHeight;

                            const bookingInfo = getSlotBookingInfo(slotTime);
                            const isSelected = bookingMode === 'multiple' && isSlotSelected(slotTime);

                            const durationClass = serviceDurationMinutes <= 30 ? 'short-duration' :
                                serviceDurationMinutes >= 90 ? 'long-duration' : '';

                            return (
                                <div
                                    key={slotTime.format('YYYY-MM-DDTHH:mm:ss')}
                                    className={`owner-time-slot ${durationClass}`}
                                    style={{
                                        position: 'absolute',
                                        top: `${totalOffset}px`,
                                        left: '2px',
                                        right: '2px',
                                        height: `${slotHeight}px`,
                                        zIndex: 2,
                                    }}
                                >
                                    <OwnerEventBlock
                                        slotTime={slotTime}
                                        slotStatus={status}
                                        bookingInfo={bookingInfo}
                                        isSelected={isSelected}
                                        serviceDuration={serviceDurationMinutes}
                                        onClick={handleSlotClick}
                                        onCancel={onCancelTraining}
                                    />
                                </div>
                            );
                        }
                    })}
                </div>
            </div>
        );
    }, [
        selectedTrainer,
        gridDimensions,
        timeRange,
        workingHours,
        bookingMode,
        serviceDurationMinutes,
        slotHeight,
        getTimeSlotsForDay,
        isMonthBlocked,
        getSlotBookingInfo,
        isSlotSelected,
        handleSlotClick,
        onCancelTraining
    ]);

    const gridLines = useMemo(() => {
        const lines = [];
        const hourCount = timeRange.totalHours;

        for (let i = 1; i <= hourCount; i++) {
            lines.push(
                <div
                    key={`grid-line-${i}`}
                    style={{
                        position: 'absolute',
                        top: `${i * gridDimensions.hourHeight}px`,
                        left: '0',
                        right: '0',
                        height: '1px',
                        background: '#e5e7eb',
                        pointerEvents: 'none',
                        zIndex: 1,
                    }}
                />
            );
        }
        return lines;
    }, [gridDimensions.hourHeight, timeRange.totalHours]);

    return (
        <div className="owner-calendar-grid">
            <div className="owner-calendar-header-fixed">
                <div className="owner-time-header-spacer"></div>
                {daysOfWeek.map((dayInfo) => {
                    const isToday = dayInfo.isToday;
                    return (
                        <div
                            key={dayInfo.date.toString()}
                            className={`owner-calendar-day-header ${isToday ? 'owner-today' : ''}`}
                        >
                            <div className="owner-day-name">{dayNamesPl[dayInfo.date.isoWeekday() - 1]}</div>
                            <div className="owner-day-date">{dayInfo.dayNumber}</div>
                        </div>
                    );
                })}
            </div>

            <div className="owner-calendar-content">
                <div className="owner-trainer-calendar-week" style={{ position: 'relative' }}>
                    {gridLines}

                    <div className="owner-time-column">
                        <div style={{ position: 'relative', height: `${gridDimensions.totalHeight}px` }}>
                            {(() => {
                                const labels = [];
                                const [startHour] = timeRange.earliestTime.split(':').map(Number);
                                const [endHour] = timeRange.latestTime.split(':').map(Number);

                                for (let hour = startHour; hour < endHour; hour++) {
                                    const index = hour - startHour;
                                    labels.push(
                                        <div
                                            key={`${hour}:00`}
                                            className="owner-time-slot-label"
                                            style={{
                                                position: 'absolute',
                                                top: `${index * gridDimensions.hourHeight + (gridDimensions.hourHeight / 2) - 10}px`,
                                                left: '0',
                                                right: '0',
                                                height: '20px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            {`${hour.toString().padStart(2, '0')}:00`}
                                        </div>
                                    );
                                }
                                return labels;
                            })()}
                        </div>
                    </div>

                    {daysOfWeek.map((dayInfo) => {
                        const isToday = dayInfo.isToday;
                        return (
                            <div
                                key={dayInfo.date.toString()}
                                className={`owner-calendar-day ${isToday ? 'owner-today' : ''}`}
                            >
                                {renderDayContent(dayInfo)}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default React.memo(OwnerCalendarGrid);